import { Search as SearchIcon } from '@mui/icons-material';
import { Popper } from '@mui/material';
import type React from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { VNFieldHelpProps } from '@/components/VNFieldHelp';
import { hasFieldHelpIcon, normalizeFieldHelpProps, VNFieldHelpLayout } from '@/components/VNFieldHelp';

import VNDropdownDisplay from './input/VNDropdownDisplay';
import VNDropdownList from './options/VNDropdownList';
import type { DisplaySlotProps, DropdownOption, FilterOption, ListSlotProps } from './types';
import { filterOptionsWithAncestors } from './types';

const POPPER_VIEWPORT_PADDING = 8;
const POPPER_MAX_HEIGHT = 400;
const INSET_SEARCH_BLOCK_HEIGHT = 56;
const MIN_LIST_VIEW_HEIGHT = 160;

export interface VNDropdownBaseProps extends VNFieldHelpProps {
  name?: string;
  options: DropdownOption[];
  value: DropdownOption['id'] | Array<DropdownOption['id']> | null;
  onChange: (value: DropdownOption['id'] | Array<DropdownOption['id']> | null) => void;
  onBlur?: () => void;
  error?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  multiple?: boolean;
  displayComponent?: React.ComponentType<DisplaySlotProps>;
  listComponent?: React.ComponentType<ListSlotProps>;
  inputRef?: React.Ref<HTMLButtonElement>;
  disableBorder?: boolean;
  compact?: boolean;
  popoverWidth?: number;
  popoverMinHeight?: number;
  className?: string;
  insetSearch?: boolean;
  /** Notifies when the inset search query changes (including clear to `''` on close). */
  onInsetQueryChange?: (query: string) => void;
  /** Propagated from VNDropdown when the original listComponent has a minSelections static marker. */
  minSelections?: number;
  filterOptions?: FilterOption[];
  isLiveLoading?: boolean;
  /**
   * Fallback options used only for display of pre-selected values that may not yet appear in the
   * main `options` array (e.g. live-search dropdowns where the initial fetch hasn't loaded the
   * selected item). These options are merged into `selectedOptions` but never shown in the list.
   */
  seedOptions?: DropdownOption[];
  allowClear?: boolean;
  /** Named chip color. `category` maps to `.tag-chip.cat` (#6897b1). */
  chipColor?: 'default' | 'category';
  allowCustom?: boolean;
  hideOptions?: boolean;
  optionTemplate?: (option: DropdownOption) => React.ReactNode;
  loadingFooterText?: string;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  /** Portal the listbox to `document.body` so it stays with the trigger on scroll. Default true. */
  usePortal?: boolean;
}

const VNDropdownBase = ({
  name,
  options,
  value,
  onChange,
  onBlur,
  error,
  label,
  placeholder,
  disabled = false,
  multiple = false,
  displayComponent: DisplayComponent = VNDropdownDisplay,
  listComponent: ListComponent = VNDropdownList,
  inputRef,
  disableBorder = false,
  compact = false,
  popoverWidth,
  popoverMinHeight,
  className,
  insetSearch = false,
  onInsetQueryChange,
  minSelections: minSelectionsProp,
  filterOptions,
  isLiveLoading = false,
  seedOptions,
  allowClear = false,
  chipColor,
  allowCustom = false,
  hideOptions = false,
  optionTemplate,
  loadingFooterText,
  inputProps,
  usePortal = true,
  ...fieldHelpProps
}: VNDropdownBaseProps) => {
  const normalized = normalizeFieldHelpProps(fieldHelpProps);
  const { dropDownItemHelperTooltip, dropDownItemHelperText } = normalized;
  const showFieldHelp = hasFieldHelpIcon(fieldHelpProps);
  const minSelections: number = minSelectionsProp ?? (ListComponent as any).minSelections ?? 0;
  const uid = useId();
  const listboxId = `vn-dropdown-listbox${uid}`;
  const triggerId = `vn-dropdown-trigger${uid}`;
  const optionIdPrefix = `vn-dropdown-option${uid}`;

  const anchorRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverMinWidthRef = useRef(336);
  const openPopperMaxHeightRef = useRef<number | null>(null);
  const paperRef = useRef<HTMLElement>(null);
  const insetSearchRef = useRef<HTMLInputElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [popperAnchorEl, setPopperAnchorEl] = useState<HTMLElement | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [popoverAlign, setPopoverAlign] = useState<'left' | 'right'>('left');
  const [insetQuery, setInsetQuery] = useState('');
  const [popoverMinWidth, setPopoverMinWidth] = useState(336);
  const formMethods = useFormContext();

  const assignTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      if (typeof inputRef === 'function') {
        inputRef(node);
      } else if (inputRef && 'current' in inputRef) {
        (inputRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      }
    },
    [inputRef]
  );

  // Latest-value ref — keeps onChange out of the auto-select effect's deps.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onInsetQueryChangeRef = useRef(onInsetQueryChange);
  onInsetQueryChangeRef.current = onInsetQueryChange;

  const setInsetQueryAndNotify = useCallback((next: string) => {
    setInsetQuery(next);
    onInsetQueryChangeRef.current?.(next);
  }, []);

  const displayedOptions = useMemo(
    () => (insetSearch ? filterOptionsWithAncestors(options, insetQuery) : options),
    [insetSearch, insetQuery, options]
  );

  useEffect(() => {
    if (insetSearch) setFocusedIndex(0);
  }, [insetSearch, insetQuery]);

  useEffect(() => {
    if (insetSearch && isOpen) {
      const id = setTimeout(() => insetSearchRef.current?.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [insetSearch, isOpen]);

  const selectedIds: Array<DropdownOption['id']> = useMemo(() => {
    if (value === null || value === undefined) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // 200ms delay after clear so re-selection doesn't feel like it ignored the interaction.
  const restoreTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optionsLoadedRef = useRef(false);
  useEffect(() => {
    if (minSelections > 0 && multiple && options.length > 0) {
      if (selectedIds.length === 0) {
        const delay = optionsLoadedRef.current ? 200 : 0;
        restoreTimerRef.current = setTimeout(() => {
          const restored = options.slice(0, minSelections).map((o) => o.id);
          onChangeRef.current?.(restored);
          if (formMethods?.resetField) {
            formMethods.resetField(name, {
              defaultValue: restored as any,
              keepDirty: false,
            });
          }
        }, delay);
      } else if (restoreTimerRef.current !== null) {
        clearTimeout(restoreTimerRef.current);
        restoreTimerRef.current = null;
      }
      optionsLoadedRef.current = true;
    }
    return () => {
      if (restoreTimerRef.current !== null) clearTimeout(restoreTimerRef.current);
    };
  }, [options, selectedIds, minSelections, multiple]);

  const selectedOptions = useMemo(() => {
    const fromOptions = options.filter((o) => selectedIds.includes(o.id));
    const foundIds = new Set(fromOptions.map((o) => o.id));
    const fromSeed = (seedOptions ?? []).filter((o) => selectedIds.includes(o.id) && !foundIds.has(o.id));
    fromSeed.forEach((o) => foundIds.add(o.id));
    const fromValue = selectedIds
      .filter((id) => !foundIds.has(id))
      .map((id) => ({ id, label: String(id) }));
    return [...fromOptions, ...fromSeed, ...fromValue];
  }, [options, seedOptions, selectedIds]);

  const computePopperMaxHeight = useCallback(
    (refBottom: number) => {
      const available = Math.max(window.innerHeight - refBottom - POPPER_VIEWPORT_PADDING, 0);
      const preferredMin = insetSearch ? INSET_SEARCH_BLOCK_HEIGHT + MIN_LIST_VIEW_HEIGHT : MIN_LIST_VIEW_HEIGHT;
      return available >= preferredMin
        ? Math.min(POPPER_MAX_HEIGHT, Math.max(available, preferredMin))
        : Math.min(POPPER_MAX_HEIGHT, available);
    },
    [insetSearch]
  );

  const handleOpen = useCallback(() => {
    if (disabled) return;
    const popperAnchor = compact ? triggerRef.current ?? anchorRef.current : anchorRef.current;
    const widthEl = anchorRef.current ?? popperAnchor;
    if (popperAnchor && widthEl) {
      const rect = popperAnchor.getBoundingClientRect();
      openPopperMaxHeightRef.current = computePopperMaxHeight(rect.bottom);
      const spaceRight = window.innerWidth - rect.right;
      setPopoverAlign(spaceRight < 320 ? 'right' : 'left');
      const slotWidth = anchorRef.current?.parentElement?.offsetWidth;
      const nextMinWidth = compact
        ? Math.max(320, slotWidth ?? 0, widthEl.offsetWidth)
        : widthEl.offsetWidth;
      popoverMinWidthRef.current = nextMinWidth;
      setPopoverMinWidth(nextMinWidth);
      setPopperAnchorEl(popperAnchor);
    }
    setIsOpen(true);
    const firstSelectedIndex = displayedOptions.findIndex((o) => selectedIds.includes(o.id));
    setFocusedIndex(firstSelectedIndex >= 0 ? firstSelectedIndex : 0);
  }, [disabled, displayedOptions, selectedIds, compact, computePopperMaxHeight]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setPopperAnchorEl(null);
    openPopperMaxHeightRef.current = null;
    setFocusedIndex(-1);
    setInsetQueryAndNotify('');
    onBlur?.();
  }, [onBlur, setInsetQueryAndNotify]);

  // The MUI invisible backdrop intercepts all clicks outside the Paper (including
  // the anchor element) because it sits at z-index 1300 in the modal portal.
  // We disable its pointer-events so anchor elements receive clicks normally, and
  // replace outside-click detection with a document-level mousedown listener.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocMouseDown = (e: MouseEvent) => {
      const insideAnchor = anchorRef.current?.contains(e.target as Node) ?? false;
      const insidePaper = paperRef.current?.contains(e.target as Node) ?? false;
      if (!insideAnchor && !insidePaper) {
        handleClose();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [isOpen, handleClose]);

  const handleToggle = useCallback(() => {
    if (isOpen) handleClose();
    else handleOpen();
  }, [isOpen, handleClose, handleOpen]);

  const handleSelect = useCallback(
    (option: DropdownOption) => {
      if (option.disabled) return;
      if (multiple) {
        const isSelected = selectedIds.includes(option.id);
        const next = isSelected ? selectedIds.filter((id) => id !== option.id) : [...selectedIds, option.id];
        onChange(next);
      } else {
        onChange(option.id);
        handleClose();
      }
    },
    [multiple, selectedIds, onChange, handleClose]
  );

  const handleClearOption = useCallback(
    (id: DropdownOption['id']) => {
      if (multiple) {
        onChange(selectedIds.filter((sid) => sid !== id));
      } else {
        onChange(null);
      }
    },
    [multiple, selectedIds, onChange]
  );

  const handleClearAll = useCallback(() => {
    onChange(multiple ? [] : null);
  }, [multiple, onChange]);

  const handleSelectAll = useCallback(
    (opts: DropdownOption[]) => {
      const allIds = opts.filter((o) => !o.disabled).map((o) => o.id);
      if (allIds.length === 0) return;
      const allSelected = allIds.every((id) => selectedIds.includes(id));
      if (allSelected) {
        onChange(selectedIds.filter((id) => !allIds.includes(id)));
      } else {
        onChange(Array.from(new Set([...selectedIds, ...allIds])));
      }
    },
    [selectedIds, onChange]
  );

  const handleKeyDown = useCallback<React.KeyboardEventHandler<HTMLButtonElement>>(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) handleOpen();
          else setFocusedIndex((prev) => Math.min(prev + 1, displayedOptions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!isOpen) handleOpen();
          else setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!isOpen) {
            handleOpen();
          } else if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            handleSelect(displayedOptions[focusedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
        case 'Tab':
          handleClose();
          break;
        default:
          break;
      }
    },
    [isOpen, displayedOptions, focusedIndex, handleOpen, handleClose, handleSelect]
  );

  const handleInsetKeyDown = useCallback<React.KeyboardEventHandler<HTMLInputElement>>(
    (e) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, displayedOptions.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0 && focusedIndex < displayedOptions.length) {
            handleSelect(displayedOptions[focusedIndex]);
            if (!multiple) return; // handleSelect calls handleClose for single-select
            setInsetQueryAndNotify('');
          }
          break;
        case 'Escape':
          e.preventDefault();
          handleClose();
          break;
        case 'Tab':
          handleClose();
          break;
        default:
          break;
      }
    },
    [displayedOptions, focusedIndex, handleSelect, handleClose, multiple, setInsetQueryAndNotify]
  );

  const focusedOptionId = isOpen && focusedIndex >= 0 ? `${optionIdPrefix}-${focusedIndex}` : undefined;

  const displayProps = useMemo<DisplaySlotProps>(
    () => ({
      selectedOptions,
      isOpen,
      onToggle: handleToggle,
      onClearOption: handleClearOption,
      label,
      placeholder,
      disabled,
      error,
      multiple,
      inputRef: assignTriggerRef,
      triggerId,
      listboxId,
      focusedOptionId,
      onKeyDown: handleKeyDown,
      disableBorder,
      compact,
      allowClear,
      onClearAll: handleClearAll,
      hideFloatingLabel: false,
      chipColor,
      persistentInput: hideOptions,
      onCustomTyped: allowCustom && !multiple ? (text) => onChange(text) : undefined,
      inputProps,
      ...fieldHelpProps,
    }),
    [
      selectedOptions,
      isOpen,
      handleToggle,
      handleClearOption,
      label,
      placeholder,
      disabled,
      error,
      multiple,
      assignTriggerRef,
      triggerId,
      listboxId,
      focusedOptionId,
      handleKeyDown,
      disableBorder,
      compact,
      allowClear,
      handleClearAll,
      chipColor,
      hideOptions,
      allowCustom,
      onChange,
      inputProps,
      fieldHelpProps,
    ]
  );

  const listProps = useMemo<ListSlotProps>(
    () => ({
      options: displayedOptions,
      selectedIds,
      onSelect: handleSelect,
      onClose: handleClose,
      onSelectAll: handleSelectAll,
      multiple,
      focusedIndex,
      listboxId,
      optionIdPrefix,
      insetQuery,
      filterOptions,
      isLiveLoading,
      dropDownItemHelperTooltip,
      dropDownItemHelperText,
      optionTemplate,
      loadingFooterText,
    }),
    [
      displayedOptions,
      selectedIds,
      handleSelect,
      handleClose,
      handleSelectAll,
      multiple,
      focusedIndex,
      listboxId,
      optionIdPrefix,
      insetQuery,
      filterOptions,
      isLiveLoading,
      dropDownItemHelperTooltip,
      dropDownItemHelperText,
      optionTemplate,
      loadingFooterText,
    ]
  );

  const popoverPaperSx = useMemo(
    () => ({
      width: popoverWidth ?? popoverMinWidth,
      ...(popoverMinHeight !== undefined && { minHeight: popoverMinHeight }),
    }),
    [popoverWidth, popoverMinWidth, popoverMinHeight]
  );

  const popperModifiers = useMemo(
    () => [
      {
        name: 'computeStyles',
        options: {
          adaptive: false,
        },
      },
      {
        name: 'eventListeners',
        enabled: true,
        options: {
          // In-document poppers already move with page scroll; extra scroll
          // updates make the list drift away from the trigger.
          scroll: usePortal,
          resize: true,
        },
      },
      {
        name: 'preventOverflow',
        enabled: true,
        options: {
          altAxis: true,
          tether: false,
          rootBoundary: 'viewport',
          padding: POPPER_VIEWPORT_PADDING,
          mainAxis: false,
        },
      },
      {
        name: 'flip',
        enabled: false,
      },
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: [0, 4],
        },
      },
      {
        name: 'limitHeightBelow',
        enabled: true,
        phase: 'beforeWrite' as const,
        requires: ['computeStyles'],
        fn: ({
          state,
        }: {
          state: {
            elements: { reference: { getBoundingClientRect: () => DOMRect } };
            styles: { popper: { maxHeight?: string; height?: string; minHeight?: string } };
          };
        }) => {
          const refBottom = state.elements.reference.getBoundingClientRect().bottom;
          const liveMaxHeight = computePopperMaxHeight(refBottom);
          const maxHeight =
            openPopperMaxHeightRef.current !== null
              ? Math.min(openPopperMaxHeightRef.current, liveMaxHeight)
              : liveMaxHeight;

          if (maxHeight > 0) {
            state.styles.popper.maxHeight = `${maxHeight}px`;
          }
        },
      },
      ...(usePortal
        ? [
            {
              name: 'sameWidth',
              enabled: true,
              phase: 'beforeWrite' as const,
              requires: ['computeStyles'],
              fn: ({
                state,
              }: {
                state: {
                  rects: { reference: { width: number } };
                  styles: { popper: { width?: string; minWidth?: string; maxWidth?: string } };
                };
              }) => {
                const width =
                  popoverWidth ?? (compact ? popoverMinWidthRef.current : state.rects.reference.width);
                state.styles.popper.width = `${width}px`;
                delete state.styles.popper.minWidth;
                delete state.styles.popper.maxWidth;
              },
            },
          ]
        : []),
    ],
    [usePortal, insetSearch, popoverWidth, compact, computePopperMaxHeight]
  );

  const dropdownContent = (
    <div className="vn-dropdown-root">
      <div
        className={[
          'vn-dropdown',
          compact ? 'vn-dropdown--compact' : '',
          hideOptions ? 'vn-dropdown--persistent-input' : '',
          className ?? '',
        ]
          .filter(Boolean)
          .join(' ')}
        ref={anchorRef}
      >
        <DisplayComponent {...displayProps} />
      </div>
      {!hideOptions && (
        <Popper
          open={isOpen}
          anchorEl={popperAnchorEl}
          placement={popoverAlign === 'right' ? 'bottom-end' : 'bottom-start'}
          disablePortal={!usePortal}
          className={[
            'vn-dropdown__popper',
            usePortal ? 'vn-dropdown__popper--portal' : '',
            insetSearch ? 'vn-dropdown__popper--inset-search' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ zIndex: usePortal ? 1400 : 1300 }}
          popperOptions={usePortal ? { strategy: 'fixed' } : undefined}
          modifiers={popperModifiers}
        >
          <div
            ref={paperRef as React.RefObject<HTMLDivElement>}
            className="vn-dropdown__paper"
            style={{
              ...popoverPaperSx,
              pointerEvents: 'auto',
            }}
          >
            {insetSearch && (
              <div className="vn-dropdown__inset-search">
                <div className="vn-dropdown__inset-search-field">
                  <SearchIcon className="vn-dropdown__inset-search-icon" fontSize="small" aria-hidden="true" />
                  <input
                    ref={insetSearchRef}
                    type="text"
                    className="vn-dropdown__inset-search-input"
                    placeholder="Search..."
                    value={insetQuery}
                    onChange={(e) => setInsetQueryAndNotify(e.target.value)}
                    onKeyDown={handleInsetKeyDown}
                    autoComplete="off"
                    aria-label="Filter options"
                  />
                </div>
              </div>
            )}
            <div className="vn-dropdown__list-scroll">
              <ListComponent {...listProps} />
            </div>
          </div>
        </Popper>
      )}
    </div>
  );

  if (showFieldHelp) {
    return (
      <VNFieldHelpLayout label={label} className="vn-field-help--dropdown" iconAttach="trigger" {...fieldHelpProps}>
        {dropdownContent}
      </VNFieldHelpLayout>
    );
  }

  return dropdownContent;
};

export default VNDropdownBase;
